import {HttpLink} from '@apollo/client';
import {onError} from '@apollo/client/link/error';
import {ApolloLink, split} from '@apollo/client';
import {InMemoryCache} from '@apollo/client/cache';
import {ApolloClient} from '@apollo/client';
import {getMainDefinition} from '@apollo/client/utilities';
import {WebSocketLink} from '@apollo/client/link/ws';
import {useCallback, useEffect, useState} from "react";
import {backendUrl, buildQueryString} from "~/utils";

export const LOGIN_ERROR_SERVER_UNAVAILABLE = 10;
export const LOGIN_ERROR_SERVER_UNEXPECTED_RESPONSE = 11;
export const LOGIN_ERROR_BAD_REQUEST = 100;
export const LOGIN_ERROR_INVALID_CREDENTIALS = 101;
export const LOGIN_ERROR_TOKEN_NOT_FOUND = 102;
export const LOGIN_ERROR_TOKEN_EXPIRED = 103;

export const LOGIN_ERROR_SOCIAL_AUTH_FAILED = 110;

export const LOGIN_ERROR_TOKEN_INVALID_TYPE = 1001;

export function createApolloClient(options) {
  const {
    introspection,

    httpEndpoint,
    useHttpGETForQueries,
    wsEndpoint,

    onRequest: requestHandler,
    onError: errorHandler,
  } = options;

  const isAbsoluteHttpEndpoint = httpEndpoint.startsWith('http') || httpEndpoint.startsWith('https');
  const httpLink = new HttpLink({
    uri: httpEndpoint,
    credentials: isAbsoluteHttpEndpoint ? 'include' : 'same-origin',
    useGETForQueries: useHttpGETForQueries,
    fetchOptions: {
      mode: isAbsoluteHttpEndpoint ? 'cors' : 'same-origin',
    }
  });

  const wsLink = wsEndpoint ? new WebSocketLink({
    uri: wsEndpoint,
    options: {
      reconnect: true,
      lazy: true
    },
  }) : undefined;

  const transportLink = wsLink ? split(
    ({query}) => isSubscriptionQuery(query),
    wsLink,
    httpLink,
  ) : httpLink;

  const possibleTypes = introspection ? introspection.possibleTypes : undefined;

  const client = new ApolloClient({
    connectToDevTools: true,
    cache: new InMemoryCache({possibleTypes}),

    link: ApolloLink.from([
      new ApolloLink((operation, forward) => {
        return requestHandler
          ? requestHandler(operation, forward)
          : forward(operation);
      }),

      onError(error => {
        if (errorHandler) {
          return errorHandler(error);
        }
      }),

      transportLink,
    ]),
  });

  client.httpLink = httpLink;
  client.wsLink = wsLink;
  return client;
}

function isSubscriptionQuery(query) {
  const definition = getMainDefinition(query);

  return (
    definition.kind === 'OperationDefinition' &&
    definition.operation === 'subscription'
  );
}


export function useFetchLogin() {
  const [state, setState] = useState({loading: false});

  const login = useCallback(({username, password}) => {
    if (!username || !password) {
      setState({
        loading: false,
        error: LOGIN_ERROR_BAD_REQUEST
      });
      return;
    }

    setState({loading: true});
    fetchLoginAPI({method: 'POST', url: '/v2/login-by-credentials', params: {username, password}})
      .then(() => setState({loading: false, data: true}))
      .catch(({errorCode}) => setState({loading: false, error: errorCode}))
  }, [setState]);

  const resetState = useCallback(() => {
    setState({loading: false});
  }, [setState]);

  return [login, state, resetState];
}

export function useFetchResetPassword() {
  const [state, setState] = useState({loading: false});

  const login = useCallback(({email}) => {
    if (!email) {
      setState({
        loading: false,
        error: LOGIN_ERROR_BAD_REQUEST
      });
      return;
    }

    setState({loading: true});
    fetchLoginAPI({method: 'POST', url: '/password/reset', params: {email}})
      .then(() => setState({loading: false, data: true}))
      .catch(({errorCode}) => setState({loading: false, error: errorCode}))
  }, [setState]);

  const resetState = useCallback(() => {
    setState({loading: false});
  }, [setState]);

  return [login, state, resetState];
}


export function useTokenData(token, expectedTokenType) {
  const [state, setState] = useState({loading: true});

  useEffect(() => {
    if (!token) {
      setState({
        loading: false,
        error: LOGIN_ERROR_TOKEN_NOT_FOUND,
      });
    }

    setState({loading: true});

    fetchLoginAPI({method: 'GET', url: '/v2/reset/' + token})
      .then((token) => {
        if (!expectedTokenType || expectedTokenType === token.type) {
          setState({
            loading: false,
            data: token
          });
        } else {
          setState({
            loading: false,
            data: token,
            error: LOGIN_ERROR_TOKEN_INVALID_TYPE
          });
        }
      })
      .catch(({errorCode}) => {
        setState({
          loading: false,
          data: null,
          error: errorCode,
        })
      });
  }, [token]);

  return state;
}


export function useFetchSetPassword() {
  const [state, setState] = useState({loading: false});

  const login = useCallback(({token, newPassword}) => {
    if (!token || !newPassword) {
      setState({
        loading: false,
        error: LOGIN_ERROR_BAD_REQUEST
      });
      return;
    }

    setState({loading: true});
    fetchLoginAPI({method: 'POST', url: '/v2/reset/' + token, params: {newPassword}})
      .then(() => setState({loading: false, data: true}))
      .catch(({errorCode}) => setState({loading: false, error: errorCode || LOGIN_ERROR_BAD_REQUEST}))
  }, [setState]);

  const resetState = useCallback(() => {
    setState({loading: false});
  }, [setState]);

  return [login, state, resetState];
}


async function fetchLoginAPI({method, url, params}) {
  method = method || 'GET';
  url = (method !== 'POST') ? url + '?' + buildQueryString(params || {}) : url;
  const headers = (method === 'POST') ? {
    'Content-Type': 'application/x-www-form-urlencoded'
  } : {};
  const body = (method === 'POST') ? buildQueryString(params || {}) : null;

  let response;
  try {
    response = await fetch(backendUrl(url), {method, headers, body});
  } catch (e) {
    throw {
      errorCode: LOGIN_ERROR_SERVER_UNAVAILABLE,
      exception: e,
    }
  }

  let json;
  try {
    json = await response.json();
  } catch (e) {
    console.log(response);
    throw {
      errorCode: LOGIN_ERROR_SERVER_UNEXPECTED_RESPONSE,
      exception: e,
    }
  }
  if (!response.ok) {
    throw {errorCode: (json.error_code || undefined)};
  }

  return json;
}
